import React, { useCallback, useEffect, useState } from "react";
import { CalendarClock, CalendarPlus, CheckCircle2, FileText, Pause, Pencil, Play, Plus, RotateCcw, ShieldCheck, Trash2, Wallet, UserRound } from "lucide-react";
import DatePicker from "./DatePicker.jsx";
import { apiFetch } from "../lib/api.js";
import { toast } from "./Toast.jsx";
import { confirmDialog } from "./confirmHost.jsx";
import { Field, Hlaska, Modal, Stitek, Segment } from "./ui.jsx";

// Plán fakturace v kartě klientky (16. 10. 2026, Láďa: fakturace se řídí z adminu,
// FAPI jen vystavuje faktury). Ukazuje, co se klientce teď pravidelně fakturuje,
// kontroly proti stavu klientky a co se bude dít (příští faktury, naplánované
// ukončení nebo změna balíčku). Pozastavit a spustit smí jen owner a CSM.
// Data: GET /api/fakturace-plan?klient=<notion_id>, akce přes /api/fakturace-pravidelna.

const fmtKc = (n) => `${Math.round(Number(n) || 0).toLocaleString("cs-CZ")} Kč`;
function fmtDen(iso) {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return `${d}. ${m}. ${y}`;
}
function zaDni(iso) {
  if (!iso) return null;
  const dnes = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
  return Math.round((Date.parse(`${String(iso).slice(0, 10)}T12:00:00Z`) - Date.parse(`${dnes}T12:00:00Z`)) / 86400000);
}
function popisZaDni(n) {
  if (n === null) return "";
  if (n === 0) return "dnes";
  if (n === 1) return "zítra";
  if (n === -1) return "včera";
  if (n < 0) return `před ${-n} dny`;
  return `za ${n} ${n < 5 ? "dny" : "dní"}`;
}
const obdobiText = (m) => (m === 1 ? "Měsíčně" : m === 3 ? "Čtvrtletně" : m === 12 ? "Ročně" : `Po ${m} měsících`);

function Box({ ikona: Ikona, popisek, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400">
        <Ikona className="h-3.5 w-3.5" /> {popisek}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}


const BALICKY = ["Koučink FLEXIBLE 1T", "Koučink FLEXIBLE 2T", "Koučink GOLD 1T", "Koučink GOLD 2T", "Koučink GOLD 3T", "Koučink PREMIUM 1T", "Koučink PREMIUM 2T"];

const dnesPraha = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });
const zitra = () => { const d = new Date(Date.now() + 86400000); return d.toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" }); };
const denVMesici = (iso) => Number(String(iso).slice(8, 10));
function pridejMesic(iso, n) {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + n, 1));
  const posledni = new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + 1, 0)).getUTCDate();
  dt.setUTCDate(Math.min(d, posledni));
  return dt.toISOString().slice(0, 10);
}
// Po změně ve fakturaci si to načtou všechny sekce karty (Plán fakturace, Naplánovaná změna balíčku).
export const FAKTURACE_ZMENA = "fw40-fakturace-zmena";
const ohlasZmenu = () => { try { window.dispatchEvent(new Event(FAKTURACE_ZMENA)); } catch { /* nic */ } };

async function postJson(url, telo) {
  const r = await apiFetch(url, { method: "POST", body: JSON.stringify(telo) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

// 19. 10. 2026 (Láďa, schválené mockupy): výjimka pro klientku, příští pravidelná faktura se posune na jiný den.
function PosunoutOkno({ p, onClose, onHotovo }) {
  const [datum, setDatum] = useState("");
  const [duvod, setDuvod] = useState("");
  const [busy, setBusy] = useState(false);
  const posunout = async () => {
    const ok = await confirmDialog({
      title: "Posunout příští fakturu ve FAPI?",
      description: `Příští faktura ${fmtKc(p.total)} se vystaví ${fmtDen(datum)} místo ${fmtDen(p.dalsi)}. Další pak každý měsíc ${denVMesici(datum)}. dne.`,
      confirmLabel: "Posunout", variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await postJson("/api/fakturace-pravidelna", { ucet: p.ucet, id: p.periodic_id, akce: "posunout", datum, duvod: duvod.trim() });
      toast(`Příští faktura posunutá na ${fmtDen(datum)}`, "success");
      ohlasZmenu();
      onHotovo();
    } catch (e) { toast(`Nepovedlo se: ${e.message}`, "error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={() => { if (!busy) onClose(); }} title="Posunout příští fakturu" size="sm"
      footer={<>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Zrušit</button>
        <button type="button" className="btn btn-primary" disabled={!datum || !duvod.trim() || busy} onClick={posunout}><CalendarClock className="h-4 w-4" />{busy ? "Posouvám…" : "Posunout ve FAPI"}</button>
      </>}>
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
          Teď vystaví FAPI (účet <b className="text-slate-900">{p.ucet}</b>) příští fakturu <b className="text-slate-900">{fmtDen(p.dalsi)}</b> na <b className="text-slate-900">{fmtKc(p.total)}</b>.
        </div>
        <div>
          <span className="label">Nový den příští faktury</span>
          <DatePicker value={datum} min={zitra()} onChange={setDatum} />
        </div>
        {datum && (
          <div className="flex items-start gap-2 rounded-2xl border border-brand-200 bg-brand-50/60 px-3 py-2.5 text-sm text-slate-700">
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            <span><b className="text-slate-900">Příští faktura {fmtDen(datum)}</b><br />Další faktury pak chodí každý měsíc {denVMesici(datum)}. dne. Částka ani balíček se nemění.</span>
          </div>
        )}
        <Field label="Proč posouváme" hint="Uloží se do historie fakturace klientky.">
          <input type="text" className="field" maxLength={200} value={duvod} onChange={(e) => setDuvod(e.target.value)} placeholder="Například: nastupuje až po operaci, domluva s Kristinou" />
        </Field>
      </div>
    </Modal>
  );
}

const PRAZDNA_FAKTURACE = { typ: "osoba", ulice: "", mesto: "", psc: "", firma: "", ico: "", dic: "" };
const fakturaceOk = (f) => !!(f && f.ulice && f.mesto && f.psc && (f.typ !== "firma" || (f.firma && f.ico)));

// 19. 10. 2026 (Láďa, schválené mockupy): klientka bez pravidelné faktury (přechod z permanentky
// na koučink, návrat, výjimka), založení ve FAPI z adminu. vychozi = { balicek, datum } z plánované změny balíčku.
export function ZalozitOkno({ klientId, klient: klientProp = null, vychozi = null, onClose, onHotovo }) {
  const [klient, setKlient] = useState(klientProp);
  const [nabidka, setNabidka] = useState(null);
  const [chybaNabidky, setChybaNabidky] = useState(null);
  const [balicek, setBalicek] = useState(vychozi?.balicek || "");
  const [kdy, setKdy] = useState(vychozi?.datum && vychozi.datum > dnesPraha() ? "den" : "hned");
  const [datum, setDatum] = useState(vychozi?.datum && vychozi.datum > dnesPraha() ? vychozi.datum : "");
  const [upravitUdaje, setUpravitUdaje] = useState(false);
  const [fakt, setFakt] = useState(PRAZDNA_FAKTURACE);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiFetch("/api/fakturace-plan?nabidka=1").then((r) => r.json())
      .then((j) => { if (j && j.ok) setNabidka(j.nabidka || []); else setChybaNabidky((j && j.error) || "Ceny se nenačetly"); })
      .catch((e) => setChybaNabidky(e.message));
    if (!klientProp && klientId) {
      apiFetch(`/api/fakturace-plan?klient=${encodeURIComponent(klientId)}`).then((r) => r.json())
        .then((j) => { if (j && j.ok) setKlient(j.klient || null); }).catch(() => null);
    }
  }, [klientId, klientProp]);
  useEffect(() => { if (klient) setFakt({ ...PRAZDNA_FAKTURACE, ...(klient.fakturace || {}) }); }, [klient]);

  const dnesIso = dnesPraha();
  const prvni = kdy === "hned" ? dnesIso : datum;
  const polozka = (nabidka || []).find((x) => x.balicek === balicek);
  const cena = polozka?.cena;
  const typ = /GOLD/.test(balicek) ? "GOLD" : /PREMIUM/.test(balicek) ? "PREMIUM" : "FLEXIBLE";
  const pocet = typ === "GOLD" ? 6 : typ === "PREMIUM" ? 12 : null;
  const druha = prvni ? pridejMesic(prvni, 1) : null;
  const posledni = pocet && druha ? pridejMesic(druha, pocet - 2) : null;
  const formular = !klient?.fakturace || upravitUdaje;
  const bezEmailu = klient && !klient.email;
  const hotovo = balicek && cena && prvni && fakturaceOk(fakt) && klient && !bezEmailu;
  const kratce = balicek.replace("Koučink ", "");

  const zalozit = async () => {
    const ok = await confirmDialog({
      title: kdy === "hned" ? "Vystavit fakturu a založit pravidelnou?" : "Naplánovat založení fakturace?",
      description: kdy === "hned"
        ? `${klient.jmeno} teď dostane fakturu ${kratce} na ${fmtKc(cena)} (splatnost 3 dny). Další faktury každý měsíc od ${fmtDen(druha)}.`
        : `${fmtDen(prvni)} ráno se ${klient.jmeno} vystaví faktura ${kratce} na ${fmtKc(cena)} a spustí se pravidelná fakturace.`,
      confirmLabel: kdy === "hned" ? "Vystavit a založit" : "Naplánovat", variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const j = await postJson("/api/fakturace-pravidelna", { akce: "zalozit", klient: klient.notion_id || klientId, balicek, prvni, fakturace: fakt });
      toast(j.hned ? `Faktura ${j.faktura?.cislo || ""} odeslaná, pravidelná fakturace běží` : `Naplánováno, první faktura ${fmtDen(prvni)}`, "success");
      ohlasZmenu();
      onHotovo?.();
    } catch (e) { toast(`Nepovedlo se: ${e.message}`, "error"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={() => { if (!busy) onClose(); }} title="Založit pravidelnou fakturu" size="md"
      footer={<>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Zrušit</button>
        <button type="button" className="btn btn-primary" disabled={!hotovo || busy} onClick={zalozit}><Plus className="h-4 w-4" />{busy ? "Zakládám…" : kdy === "hned" ? "Založit ve FAPI" : "Naplánovat"}</button>
      </>}>
      <div className="space-y-4">
        <div>
          <span className="label">Balíček</span>
          {chybaNabidky ? <Hlaska ton="problem" title="Ceny z FAPI se nenačetly">{chybaNabidky}</Hlaska>
            : !nabidka ? <div className="text-sm text-slate-400">Načítám ceny z FAPI…</div> : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {nabidka.map((x) => (
                <button key={x.balicek} type="button" disabled={!x.cena} onClick={() => setBalicek(x.balicek)}
                  className={`flex items-center justify-between gap-2 rounded-2xl border px-3 py-2.5 text-left disabled:opacity-50 ${balicek === x.balicek ? "border-brand bg-brand-50 ring-2 ring-brand/30" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
                  <span className="text-sm font-semibold text-slate-900">{x.balicek.replace("Koučink ", "")}</span>
                  <span className="text-sm tabular-nums text-slate-600">{x.cena ? `${fmtKc(x.cena)} / měs.` : "není ve FAPI"}</span>
                </button>
              ))}
            </div>
          )}
          <div className="mt-1 text-xs text-slate-500">Ceny podle produktů ve FAPI (účet Firma). Jinou cenu nastavíš po založení přes Upravit fakturu.</div>
        </div>
        <div>
          <span className="label">První faktura</span>
          <Segment value={kdy} onChange={setKdy} aria-label="První faktura" items={[{ key: "hned", label: "Vystavit hned" }, { key: "den", label: "Až v určitý den" }]} />
          {kdy === "den" && <div className="mt-2"><DatePicker value={datum} min={zitra()} onChange={setDatum} /></div>}
        </div>
        <div>
          <span className="label">Fakturovat na</span>
          {!klient ? <div className="text-sm text-slate-400">Načítám…</div> : formular ? (
            <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3">
              {!klient.fakturace && <div className="text-sm text-amber-800">Fakturační údaje jsme u klientky nenašli, doplň je.</div>}
              <Segment value={fakt.typ} onChange={(t) => setFakt({ ...fakt, typ: t })} aria-label="Fakturovat na" items={[{ key: "osoba", label: "Osoba" }, { key: "firma", label: "Firma" }]} />
              {fakt.typ === "firma" && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input type="text" className="field" placeholder="Název firmy" value={fakt.firma} onChange={(e) => setFakt({ ...fakt, firma: e.target.value })} />
                  <input type="text" className="field" placeholder="IČO" value={fakt.ico} onChange={(e) => setFakt({ ...fakt, ico: e.target.value })} />
                </div>
              )}
              <input type="text" className="field" placeholder="Ulice a číslo" value={fakt.ulice} onChange={(e) => setFakt({ ...fakt, ulice: e.target.value })} />
              <div className="grid grid-cols-[1fr_7rem] gap-2">
                <input type="text" className="field" placeholder="Město" value={fakt.mesto} onChange={(e) => setFakt({ ...fakt, mesto: e.target.value })} />
                <input type="text" className="field" placeholder="PSČ" value={fakt.psc} onChange={(e) => setFakt({ ...fakt, psc: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
              <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">{fakt.typ === "firma" ? <><b>{fakt.firma}</b>, IČO {fakt.ico}</> : <b>{klient.jmeno}</b>}, {fakt.ulice}, {fakt.psc} {fakt.mesto}<br /><span className="text-slate-500">{klient.email}</span></span>
              <button type="button" className="btn btn-secondary !min-h-0 shrink-0 !py-1.5 text-sm" onClick={() => setUpravitUdaje(true)}><Pencil className="h-4 w-4" /> Upravit</button>
            </div>
          )}
          {bezEmailu && <Hlaska ton="pozor" title="Klientka nemá e‑mail" className="mt-2">Doplň ho v kartě klientky, jinak faktura nedojde.</Hlaska>}
        </div>
        {balicek && cena && prvni && (
          <div className="rounded-2xl border border-brand-200 bg-brand-50/60 p-3 text-sm text-slate-700">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand-deep">Co se stane</div>
            <ul className="space-y-1">
              <li>• {kdy === "hned" ? <>Hned se vystaví první faktura <b>{balicek}</b> na <b>{fmtKc(cena)}</b>, splatnost 3 dny.</> : <><b>{fmtDen(prvni)}</b> ráno se vystaví první faktura <b>{balicek}</b> na <b>{fmtKc(cena)}</b>.</>}</li>
              <li>• Další faktury chodí každý měsíc {denVMesici(druha)}. dne od {fmtDen(druha)}{posledni ? <>, celkem {pocet} faktur, poslední {fmtDen(posledni)}</> : ", bez konce"}. FAPI účet <b>Firma</b>.</li>
              {klient?.balicek !== balicek && <li>• Balíček v adminu se přepne{klient?.balicek ? <> z <b>{klient.balicek}</b></> : null} na <b>{balicek}</b>.</li>}
              <li>• Systém předtím zkontroluje, že klientka nemá fakturu za koučink z posledních 10 dnů ani nezaplacenou.</li>
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

// 29. 10. 2026 (hlášení #180, Kristina, schválené mockupy): jednorázová faktura za permanentku.
// Výchozí je standard (10 tréninků za běžnou cenu z FAPI, splatnost 3 dny, účet Firma). Účet
// (Firma nebo Láďa), počet, cenu i splatnost jde upravit. Pravidelnou fakturu nezakládá.
// Data: GET /api/fakturace-plan?nabidka=1&permanentka=1 a ?klient=, vystavení POST /api/fakturace-pravidelna { akce: "permanentka" }.
export function JednorazovaOkno({ klientId, klient: klientProp = null, maPravidelnou = false, onClose, onHotovo }) {
  const [klient, setKlient] = useState(klientProp);
  const [ceny, setCeny] = useState(null); // { Firma: { cena }, Lada: { cena } | null }
  const [ucet, setUcet] = useState("Firma");
  const [pocet, setPocet] = useState("10");
  const [cena, setCena] = useState("");
  const [splatnost, setSplatnost] = useState(() => { const [y, m, d] = dnesPraha().split("-").map(Number); return new Date(Date.UTC(y, m - 1, d + 3)).toISOString().slice(0, 10); });
  const [upravitUdaje, setUpravitUdaje] = useState(false);
  const [fakt, setFakt] = useState(PRAZDNA_FAKTURACE);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiFetch("/api/fakturace-plan?nabidka=1&permanentka=1").then((r) => r.json())
      .then((j) => { const p = (j && j.ok && j.permanentka) || {}; setCeny(p); if (p.Firma && p.Firma.cena) setCena((c) => c || String(p.Firma.cena)); }).catch(() => setCeny({}));
    if (!klientProp && klientId) {
      apiFetch(`/api/fakturace-plan?klient=${encodeURIComponent(klientId)}`).then((r) => r.json())
        .then((j) => { if (j && j.ok) setKlient(j.klient || null); }).catch(() => null);
    }
  }, [klientId, klientProp]);
  useEffect(() => { if (klient) setFakt({ ...PRAZDNA_FAKTURACE, ...(klient.fakturace || {}) }); }, [klient]);
  const standard = ceny && ceny[ucet] ? ceny[ucet] : null;
  const zmenUcet = (u) => {
    // Cena se přepíše na standard nového účtu jen tehdy, když nebyla ručně změněná.
    const stara = ceny && ceny[ucet] && Number(ceny[ucet].cena);
    if (ceny && ceny[u] && ceny[u].cena && (!cena || Number(cena) === stara)) setCena(String(ceny[u].cena));
    setUcet(u);
  };
  const n = /^\d+$/.test(String(pocet).trim()) ? Number(pocet) : 0;
  const kc = Number(String(cena).replace(/\s+/g, "")) || 0;
  const polozka = `Permanentka: ${n} osobních tréninků`;
  const jeStandard = standard && n === 10 && kc === Number(standard.cena);
  const formular = !klient?.fakturace || upravitUdaje;
  const bezEmailu = klient && !klient.email;
  const hotovo = n > 0 && n <= 50 && kc > 0 && splatnost && fakturaceOk(fakt) && klient && !bezEmailu && standard;
  const vystavit = async () => {
    const ok = await confirmDialog({
      title: "Vystavit jednorázovou fakturu?",
      description: `${klient.jmeno} teď dostane e‑mailem fakturu ${polozka} na ${fmtKc(kc)} ze účtu ${ucet === "Lada" ? "Láďa" : "Firma"}, splatnost ${fmtDen(splatnost)}.`,
      confirmLabel: "Vystavit a poslat", variant: "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const j = await postJson("/api/fakturace-pravidelna", { akce: "permanentka", klient: klient.notion_id || klientId, ucet, pocet: n, cena: kc, splatnost, fakturace: fakt });
      toast(`Faktura ${j.faktura?.cislo || ""} odeslaná`, "success");
      ohlasZmenu();
      onHotovo?.();
    } catch (e) { toast(`Nepovedlo se: ${e.message}`, "error"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={() => { if (!busy) onClose(); }} title="Jednorázová faktura: permanentka" size="md"
      footer={<>
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={onClose}>Zrušit</button>
        <button type="button" className="btn btn-primary" disabled={!hotovo || busy} onClick={vystavit}><FileText className="h-4 w-4" />{busy ? "Vystavuji…" : "Vystavit a poslat"}</button>
      </>}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-1.5">
          <Stitek ton={jeStandard ? "ok" : "pozor"} velikost="mala">{jeStandard ? "Standard" : "Upraveno"}</Stitek>
          {standard && <Stitek ton="neutralni" velikost="mala">Běžně 10 tréninků za {fmtKc(standard.cena)}</Stitek>}
        </div>
        <div>
          <span className="label">FAPI účet</span>
          <Segment value={ucet} onChange={zmenUcet} aria-label="FAPI účet" items={[{ key: "Firma", label: "Firma" }, { key: "Lada", label: "Láďa" }]} />
          {ceny && !standard && <Hlaska ton="pozor" title="Na tomhle účtu není produkt permanentky" className="mt-2">Ve FAPI účtu {ucet === "Lada" ? "Láďa" : "Firma"} chybí produkt „Permanentka: 10 osobních tréninků“. Vyber druhý účet, nebo produkt založ ve FAPI.</Hlaska>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Počet tréninků">
            <input type="number" inputMode="numeric" min="1" max="50" className="field" value={pocet} onChange={(e) => setPocet(e.target.value)} />
          </Field>
          <Field label="Cena celkem (Kč)">
            <input type="number" inputMode="numeric" min="1" className="field" value={cena} onChange={(e) => setCena(e.target.value)} />
          </Field>
        </div>
        <Field label="Splatnost" hint="Běžně 3 dny od vystavení.">
          <DatePicker value={splatnost} min={dnesPraha()} onChange={setSplatnost} />
        </Field>
        <div>
          <span className="label">Fakturovat na</span>
          {!klient ? <div className="text-sm text-slate-400">Načítám…</div> : formular ? (
            <div className="space-y-2 rounded-2xl border border-slate-200 bg-white p-3">
              {!klient.fakturace && <div className="text-sm text-amber-800">Fakturační údaje jsme u klientky nenašli, doplň je.</div>}
              <Segment value={fakt.typ} onChange={(t) => setFakt({ ...fakt, typ: t })} aria-label="Fakturovat na" items={[{ key: "osoba", label: "Osoba" }, { key: "firma", label: "Firma" }]} />
              {fakt.typ === "firma" && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input type="text" className="field" placeholder="Název firmy" value={fakt.firma} onChange={(e) => setFakt({ ...fakt, firma: e.target.value })} />
                  <input type="text" className="field" placeholder="IČO" value={fakt.ico} onChange={(e) => setFakt({ ...fakt, ico: e.target.value })} />
                </div>
              )}
              <input type="text" className="field" placeholder="Ulice a číslo" value={fakt.ulice} onChange={(e) => setFakt({ ...fakt, ulice: e.target.value })} />
              <div className="grid grid-cols-[1fr_7rem] gap-2">
                <input type="text" className="field" placeholder="Město" value={fakt.mesto} onChange={(e) => setFakt({ ...fakt, mesto: e.target.value })} />
                <input type="text" className="field" placeholder="PSČ" value={fakt.psc} onChange={(e) => setFakt({ ...fakt, psc: e.target.value })} />
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
              <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <span className="min-w-0 flex-1">{fakt.typ === "firma" ? <><b>{fakt.firma}</b>, IČO {fakt.ico}</> : <b>{klient.jmeno}</b>}, {fakt.ulice}, {fakt.psc} {fakt.mesto}<br /><span className="text-slate-500">{klient.email}</span></span>
              <button type="button" className="btn btn-secondary !min-h-0 shrink-0 !py-1.5 text-sm" onClick={() => setUpravitUdaje(true)}><Pencil className="h-4 w-4" /> Upravit</button>
            </div>
          )}
          {bezEmailu && <Hlaska ton="pozor" title="Klientka nemá e‑mail" className="mt-2">Doplň ho v kartě klientky, jinak faktura nedojde.</Hlaska>}
        </div>
        {n > 0 && kc > 0 && (
          <div className="rounded-2xl border border-brand-200 bg-brand-50/60 p-3 text-sm text-slate-700">
            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand-deep">Co se stane</div>
            <ul className="space-y-1">
              <li>• Hned se vystaví faktura <b>{polozka}</b> na <b>{fmtKc(kc)}</b> z účtu <b>{ucet === "Lada" ? "Láďa" : "Firma"}</b>, splatnost <b>{fmtDen(splatnost)}</b>, a odejde klientce e‑mailem.</li>
              <li>• Pravidelná faktura se nezakládá, je to jednorázová platba.</li>
              <li>• Systém předtím zkontroluje, že klientka nemá fakturu za permanentku z posledních 10 dnů.</li>
              <li>• Po zaplacení se klientce připíše <b>{n}</b> {n === 1 ? "trénink" : n < 5 ? "tréninky" : "tréninků"} k odchození.</li>
              {maPravidelnou
                ? <li>• Klientce běží pravidelná faktura, balíček v adminu zůstává <b>{klient?.balicek || "beze změny"}</b> (permanentka jsou tréninky navíc).</li>
                : klient?.balicek && !/permanent/i.test(klient.balicek) && <li>• Balíček v adminu se přepne z <b>{klient.balicek}</b> na <b>Permanentka</b>.</li>}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}

// 19. 10. 2026 (Láďa): klientka odchodila víc, než bylo zaplaceno (1T −2, 2T −4, 3T −6).
// Návrh vystavit příští fakturu dřív (zítra), další faktury i konec spolupráce se posunou.
// V testovacím provozu (listopad 2026) schvaluje owner, ostatní návrh jen vidí.
function DriveKarta({ k, muzeSchvalit, busy, onSchvalit, onOdmitnout }) {
  const d = k.drive || {};
  return (
    <div className="flex flex-wrap items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
      <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
      <div className="min-w-0 flex-1 basis-56">
        <div className="text-sm font-bold text-amber-900">{k.nadpis}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Stitek ton="problem" velikost="mala">Stav tréninků {String(d.stav).replace("-", "−")}</Stitek>
          <Stitek ton="neutralni" velikost="mala">{d.tydne}T, hranice {String(d.prah).replace("-", "−")}</Stitek>
          <Stitek ton="pozor" velikost="mala">Faktura {fmtDen(d.dalsi)} → {fmtDen(d.nove)}</Stitek>
          {d.konec && d.konecNovy && <Stitek ton="neutralni" velikost="mala">Konec {fmtDen(d.konec)} → {fmtDen(d.konecNovy)}</Stitek>}
        </div>
        <div className="mt-1.5 text-sm text-amber-800">{k.text}</div>
      </div>
      {muzeSchvalit && (
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:shrink-0 sm:flex-row">
          <button type="button" className="btn btn-primary w-full sm:w-auto sm:!min-h-0 sm:!py-1.5 sm:text-sm" disabled={busy} onClick={onSchvalit}><CalendarClock className="h-4 w-4" /> Vystavit dřív</button>
          <button type="button" className="btn btn-secondary w-full sm:w-auto sm:!min-h-0 sm:!py-1.5 sm:text-sm" disabled={busy} onClick={onOdmitnout}>Nechat podle plánu</button>
        </div>
      )}
    </div>
  );
}

// Úprava pravidelné faktury: název položky (balíček) a cena. Platí od příští faktury.
function UpravitFakturuOkno({ p, onClose, onUlozit, busy }) {
  const hlavni = (p.polozky || []).find((x) => !/zaokrouhl/i.test(x.nazev || "")) || { nazev: "", cena: 0 };
  const [nazev, setNazev] = useState(hlavni.nazev);
  const [vlastni, setVlastni] = useState(false);
  const [menitCenu, setMenitCenu] = useState(false);
  const [cena, setCena] = useState(String(hlavni.cena || ""));
  // 3. 11. 2026 (hlášení #192): při změně balíčku se cena předvyplní z ceníku FAPI
  // (stejný zdroj jako Naplánovaná změna balíčku) a jde dál upravit.
  const [nabidka, setNabidka] = useState(null);
  const [chybaCeniku, setChybaCeniku] = useState(false);
  useEffect(() => {
    let zije = true;
    apiFetch("/api/fakturace-plan?nabidka=1").then((r) => r.json())
      .then((j) => { if (!zije) return; if (j && j.ok) setNabidka(j.nabidka || []); else setChybaCeniku(true); })
      .catch(() => { if (zije) setChybaCeniku(true); });
    return () => { zije = false; };
  }, []);
  // Ceník dorazil až po výběru balíčku: dovyplnit, pokud cenu ještě nikdo neměnil.
  useEffect(() => {
    if (!nabidka || vlastni || nazev === hlavni.nazev || menitCenu) return;
    const x = nabidka.find((n) => n.balicek === nazev);
    if (x && x.cena > 0) { setMenitCenu(true); setCena(String(x.cena)); }
  }, [nabidka]); // eslint-disable-line react-hooks/exhaustive-deps
  const cenaCeniku = (b) => { const x = (nabidka || []).find((n) => n.balicek === b); return x && x.cena > 0 ? Number(x.cena) : null; };
  const vyberBalicek = (b) => {
    setNazev(b);
    if (b === hlavni.nazev) { setMenitCenu(false); setCena(String(hlavni.cena || "")); return; }
    const c = cenaCeniku(b);
    if (c != null) { setMenitCenu(true); setCena(String(c)); }
  };
  const cenaZCeniku = !vlastni ? cenaCeniku(nazev) : null;
  const moznosti = !hlavni.nazev || BALICKY.includes(hlavni.nazev) ? BALICKY : [hlavni.nazev, ...BALICKY];
  const zmena = (nazev.trim() && nazev.trim() !== hlavni.nazev) || (menitCenu && Number(cena) !== Number(hlavni.cena));
  return (
    <Modal open onClose={() => { if (!busy) onClose(); }} title="Upravit pravidelnou fakturu" size="sm"
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Zrušit</button>
        <button type="button" className="btn btn-primary" disabled={!zmena || busy} onClick={() => onUlozit({ nazev: nazev.trim(), cena: menitCenu ? Number(cena) : null })}>{busy ? "Ukládám…" : "Uložit do FAPI"}</button>
      </>}>
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
          FAPI účet <b className="text-slate-900">{p.ucet}</b>, teď se fakturuje <b className="text-slate-900">{hlavni.nazev}</b> za <b className="text-slate-900">{fmtKc(hlavni.cena)}</b>.
        </div>
        <Field label="Položka na faktuře" hint={vlastni ? "Tenhle text uvidí klientka na faktuře." : null}>
          <select className="field" value={vlastni ? "__vlastni__" : nazev}
            onChange={(e) => { if (e.target.value === "__vlastni__") { setVlastni(true); } else { setVlastni(false); vyberBalicek(e.target.value); } }}>
            {moznosti.map((b) => <option key={b} value={b}>{b}</option>)}
            <option value="__vlastni__">Vlastní text…</option>
          </select>
          {vlastni && <input type="text" className="field mt-2" maxLength={200} value={nazev} onChange={(e) => setNazev(e.target.value)} placeholder="Text položky na faktuře" />}
        </Field>
        <div>
          <span className="label">Cena</span>
          {menitCenu ? (
            <>
              <input type="number" min="0" step="10" className="field" value={cena} onChange={(e) => setCena(e.target.value)} />
              {cenaZCeniku != null && nazev !== hlavni.nazev && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[13px] text-slate-500">
                  <Stitek ton="znacka" velikost="mala">ceník {fmtKc(cenaZCeniku)}</Stitek>
                  {Number(cena) === cenaZCeniku ? "Cena podle ceníku, můžeš ji upravit." : "Cena se liší od ceníku."}
                </div>
              )}
              {chybaCeniku && nazev !== hlavni.nazev && <div className="mt-1.5 text-[13px] text-amber-700">Ceník se nenačetl, cenu zkontroluj ručně.</div>}
            </>
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
              <span className="text-sm font-semibold text-slate-900">{fmtKc(hlavni.cena)} <span className="font-normal text-slate-500">beze změny</span></span>
              <button type="button" className="btn btn-secondary !min-h-0 !py-1.5 text-sm" onClick={() => setMenitCenu(true)}>Změnit cenu</button>
            </div>
          )}
        </div>
        <div className="text-sm text-slate-500">Změna platí od příští faktury {fmtDen(p.dalsi)}. Už vystavené faktury se nemění.</div>
      </div>
    </Modal>
  );
}

export default function PlanFakturaceSection({ klientId, canManage = false }) {
  const [data, setData] = useState(null);
  const [chyba, setChyba] = useState(null);
  const [busy, setBusy] = useState(false);
  const [upravit, setUpravit] = useState(null);
  const [posunout, setPosunout] = useState(null);
  const [zalozit, setZalozit] = useState(false);
  const [jednorazova, setJednorazova] = useState(false);

  const nacti = useCallback(() => {
    if (!klientId) return;
    apiFetch(`/api/fakturace-plan?klient=${encodeURIComponent(klientId)}`)
      .then((r) => r.json())
      .then((j) => { if (j && j.ok) { setData(j); setChyba(null); } else setChyba((j && j.error) || "Nepodařilo se načíst"); })
      .catch((e) => setChyba(e.message));
  }, [klientId]);
  useEffect(() => { nacti(); }, [nacti]);
  useEffect(() => {
    window.addEventListener(FAKTURACE_ZMENA, nacti);
    return () => window.removeEventListener(FAKTURACE_ZMENA, nacti);
  }, [nacti]);

  if (chyba) return <Hlaska ton="problem" title="Plán fakturace se nenačetl">{chyba}</Hlaska>;
  if (!data) return <div className="text-sm text-slate-400">Načítám…</div>;

  const faktury = data.pravidelne || [];
  const kontroly = data.kontroly || [];
  const plan = data.plan || [];
  const zalozeni = data.zalozeni || [];
  const lzeZalozit = canManage && faktury.length === 0 && zalozeni.length === 0;

  const zmenit = async (p, akce) => {
    if (!p || busy) return;
    const zastavit = akce === "zastavit";
    const ok = await confirmDialog({
      title: zastavit ? "Pozastavit fakturaci?" : "Spustit fakturaci?",
      description: zastavit
        ? `Pravidelná faktura ve FAPI (účet ${p.ucet}) se pozastaví a další faktura ${fmtDen(p.dalsi)} neodejde. Kdykoli ji tu zase spustíš.`
        : `Pravidelná faktura ve FAPI (účet ${p.ucet}) se znovu spustí a faktury budou chodit podle plánu.`,
      confirmLabel: zastavit ? "Pozastavit" : "Spustit",
      variant: zastavit ? "danger" : "primary",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await apiFetch("/api/fakturace-pravidelna", { method: "POST", body: JSON.stringify({ ucet: p.ucet, id: p.periodic_id, akce }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      toast(zastavit ? "Fakturace pozastavená" : "Fakturace spuštěná", "success");
      nacti();
    } catch (e) {
      toast(`Nepovedlo se: ${e.message}`, "error");
    } finally { setBusy(false); }
  };

  const post = async (url, telo, hotovo) => {
    setBusy(true);
    try {
      const r = await apiFetch(url, { method: "POST", body: JSON.stringify(telo) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
      toast(hotovo, "success");
      nacti();
      return true;
    } catch (e) {
      toast(`Nepovedlo se: ${e.message}`, "error");
      return false;
    } finally { setBusy(false); }
  };
  const ulozitUpravu = async (p, { nazev, cena }) => {
    if (busy) return;
    setBusy(true);
    const ok = await confirmDialog({
      title: "Uložit změnu faktury do FAPI?",
      description: `Od příští faktury ${fmtDen(p.dalsi)} bude na faktuře „${nazev}“${cena !== null ? ` za ${fmtKc(cena)}` : ", cena beze změny"}.`,
      confirmLabel: "Uložit", variant: "primary",
    });
    setBusy(false);
    if (!ok) return;
    if (await post("/api/fakturace-pravidelna", { ucet: p.ucet, id: p.periodic_id, akce: "upravit", nazev, cena }, "Faktura upravená")) setUpravit(null);
  };
  const driveSchvalit = async (k) => {
    const d = k.drive || {};
    const ok = await confirmDialog({ title: "Vystavit fakturu dřív?", description: `Příští faktura se vystaví ${fmtDen(d.nove)} místo ${fmtDen(d.dalsi)}. Další faktury${d.konecNovy ? " i konec spolupráce" : ""} se posunou o stejný počet dní a klientce přijde e‑mail s oznámením.`, confirmLabel: "Vystavit dřív", variant: "primary" });
    if (ok && await post("/api/fakturace-pravidelna", { akce: "drive_schvalit", id: d.id }, `Faktura se vystaví ${fmtDen(d.nove)}`)) ohlasZmenu();
  };
  const driveOdmitnout = async (k) => {
    const ok = await confirmDialog({ title: "Nechat fakturu podle plánu?", description: `Faktura zůstane ${fmtDen((k.drive || {}).dalsi)}. Do další faktury se návrh znovu neobjeví.`, confirmLabel: "Nechat podle plánu", variant: "primary" });
    if (ok && await post("/api/fakturace-pravidelna", { akce: "drive_odmitnout", id: (k.drive || {}).id }, "Faktura zůstává podle plánu")) ohlasZmenu();
  };
  const zalozeniZnovu = async (z) => {
    const ok = await confirmDialog({ title: "Dokončit založení fakturace?", description: z.faktura ? `První faktura ${z.faktura.cislo || ""} už je vystavená, dokončí se jen pravidelná faktura.` : `Vystaví se první faktura ${z.balicek} a spustí pravidelná fakturace.`, confirmLabel: "Dokončit", variant: "primary" });
    if (ok && await post("/api/fakturace-pravidelna", { akce: "zalozit_znovu", id: z.id }, "Fakturace založená")) ohlasZmenu();
  };
  const zalozeniZrusit = async (z) => {
    const ok = await confirmDialog({ title: "Zrušit naplánované založení?", description: `První faktura ${z.balicek} ${fmtDen(z.prvni)} se nevystaví.`, confirmLabel: "Zrušit založení", variant: "danger" });
    if (ok && await post("/api/fakturace-pravidelna", { akce: "zalozit_zrusit", id: z.id }, "Založení zrušené")) ohlasZmenu();
  };
  const vyjimka = (k, zrusit) => post("/api/fakturace-plan", { akce: zrusit ? "zrusit_vyjimku" : "vyjimka", klient: klientId, klic: k.klic }, zrusit ? "Výjimka zrušená" : "Označeno jako výjimka");

  return (
    <div className="space-y-3">
      {faktury.length === 0 && <div className="flex flex-wrap gap-1.5"><Stitek ton="neutralni">Bez pravidelné faktury</Stitek></div>}
      {faktury.map((p) => {
        const bezi = p.status === "active";
        return (
          <div key={`${p.ucet}-${p.periodic_id}`} className="space-y-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <Stitek ton={bezi ? "ok" : "pozor"} ikona={bezi ? CheckCircle2 : Pause}>{bezi ? "Fakturace běží" : "Fakturace pozastavená"}</Stitek>
              <Stitek ton="neutralni">FAPI účet {p.ucet}</Stitek>
              <Stitek ton="neutralni">{obdobiText(p.mesicu)}</Stitek>
            </div>
            {(
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Box ikona={FileText} popisek="Fakturuje se">
                  {(p.polozky || []).filter((x) => !/zaokrouhl/i.test(x.nazev || "")).map((x, i) => (
                    <div key={i} className="text-sm font-semibold text-slate-900">
                      {x.nazev}{x.ks > 1 ? ` × ${x.ks}` : ""}
                    </div>
                  ))}
                </Box>
                <Box ikona={Wallet} popisek="Částka">
                  <div className="text-lg font-bold tabular-nums text-slate-900 demo-sensitive">{fmtKc(p.castka_mesic)} <span className="text-sm font-semibold text-slate-500">/ měsíc</span></div>
                  {p.mesicu > 1 && <div className="text-xs text-slate-500 demo-sensitive">Faktura {fmtKc(p.total)} za {p.mesicu} měsíce</div>}
                </Box>
                <Box ikona={CalendarClock} popisek="Příští faktura">
                  {bezi ? (
                    <>
                      <div className="text-lg font-bold text-slate-900">{fmtDen(p.dalsi)}</div>
                      <div className="text-xs text-slate-500">{popisZaDni(zaDni(p.dalsi))}</div>
                    </>
                  ) : <div className="text-sm font-semibold text-amber-700">Nevystaví se, dokud fakturaci nespustíš</div>}
                </Box>
              </div>
            )}
          </div>
        );
      })}

      {kontroly.filter((k) => k.klic === "drive").map((k) => (
        <DriveKarta key="drive" k={k} muzeSchvalit={!!data.muze_schvalit_drive} busy={busy} onSchvalit={() => driveSchvalit(k)} onOdmitnout={() => driveOdmitnout(k)} />
      ))}
      {kontroly.filter((k) => k.klic !== "drive").length > 0
        ? kontroly.filter((k) => k.klic !== "drive").map((k, i) => (k.vyjimka
          ? <div key={i} className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              <ShieldCheck className="h-5 w-5 shrink-0 text-slate-500" />
              <span className="min-w-0 flex-1"><b className="text-slate-800">{k.nadpis}</b>, výjimka: řešíme ručně ({k.vyjimka.kdo}, {fmtDen(k.vyjimka.kdy)})</span>
              {canManage && <button type="button" className="btn btn-secondary !min-h-0 !py-1.5 text-sm" disabled={busy} onClick={() => vyjimka(k, true)}>Zrušit výjimku</button>}
            </div>
          : <Hlaska key={i} ton={k.ton || "pozor"} title={k.nadpis}
              akce={canManage && k.ton === "pozor" && k.klic ? <button type="button" className="btn btn-secondary !min-h-0 shrink-0 !py-1.5 text-sm" disabled={busy} onClick={() => vyjimka(k, false)}><ShieldCheck className="h-4 w-4" /> Výjimka, řešíme ručně</button> : null}>{k.text}</Hlaska>))
        : faktury.length > 0 && !kontroly.length && <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Fakturace odpovídá stavu klientky.</div>}

      {zalozeni.map((z) => (z.stav === "chyba"
        ? <Hlaska key={z.id} ton="problem" title={`Založení fakturace ${z.balicek.replace("Koučink ", "")} se nepovedlo`}
            akce={canManage ? <div className="flex shrink-0 flex-col gap-2 sm:flex-row"><button type="button" className="btn btn-primary !min-h-0 shrink-0 !py-1.5 text-sm" disabled={busy} onClick={() => zalozeniZnovu(z)}><RotateCcw className="h-4 w-4" /> Zkusit znovu</button>{!z.faktura && <button type="button" className="btn btn-secondary !min-h-0 shrink-0 !py-1.5 text-sm" disabled={busy} onClick={() => zalozeniZrusit(z)}><Trash2 className="h-4 w-4" /> Zrušit</button>}</div> : null}>
            {z.chyba}{z.faktura ? ` První faktura ${z.faktura.cislo || ""} je vystavená.` : ""}
          </Hlaska>
        : <div key={z.id} className="flex flex-wrap items-start gap-2 rounded-2xl border border-brand-200 bg-brand-50/60 px-4 py-3 text-sm text-slate-700">
            <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <div className="min-w-0 flex-1 basis-56">
              <div className="font-semibold text-slate-900">Naplánované založení fakturace</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <Stitek ton="neutralni" velikost="mala">{z.balicek.replace("Koučink ", "")}</Stitek>
                <Stitek ton="ok" velikost="mala">1. faktura {fmtDen(z.prvni)} ráno</Stitek>
                <Stitek ton="neutralni" velikost="mala"><UserRound className="h-3 w-3" />{z.kdo || "admin"}, {fmtDen(z.zalozeno)}</Stitek>
              </div>
            </div>
            {canManage && z.stav === "ceka" && z.prvni <= dnesPraha() && <button type="button" className="btn btn-primary !min-h-0 !py-1.5 text-sm" disabled={busy} onClick={() => zalozeniZnovu(z)}><Play className="h-4 w-4" /> Vystavit teď</button>}
            {canManage && z.stav === "ceka" && <button type="button" className="btn btn-secondary !min-h-0 !py-1.5 text-sm" disabled={busy} onClick={() => zalozeniZrusit(z)}><Trash2 className="h-4 w-4" /> Zrušit</button>}
          </div>))}

      {plan.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">Co se bude dít</div>
          <ul className="divide-y divide-slate-100">
            {plan.map((r, i) => (
              <li key={i} className="flex items-start gap-3 px-3 py-2.5">
                <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${r.ton === "problem" ? "bg-rose-500" : r.ton === "pozor" ? "bg-amber-500" : "bg-brand"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900">{r.text}</div>
                  {r.popis && <div className="text-xs text-slate-500">{r.popis}</div>}
                </div>
                <div className="shrink-0 whitespace-nowrap text-right">
                  <div className="text-sm font-semibold text-slate-700">{fmtDen(r.datum)}</div>
                  <div className="text-xs text-slate-400">{popisZaDni(zaDni(r.datum))}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {lzeZalozit && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setZalozit(true)}><Plus className="h-4 w-4" /> Založit pravidelnou fakturu</button>
        </div>
      )}
      {canManage && (
        <div className="flex flex-wrap gap-2 pt-1">
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setJednorazova(true)}><FileText className="h-4 w-4" /> Jednorázová faktura</button>
        </div>
      )}
      {canManage && faktury.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-1">
          {faktury.map((p, i) => <button key={`u-${p.ucet}-${p.periodic_id}`} type="button" className="btn btn-primary" disabled={busy} onClick={() => setUpravit(i)}><Pencil className="h-4 w-4" /> Upravit fakturu{faktury.length > 1 ? ` (${p.ucet})` : ""}</button>)}
          {faktury.filter((p) => p.status === "active").map((p, i) => <button key={`p-${p.ucet}-${p.periodic_id}`} type="button" className="btn btn-secondary" disabled={busy} onClick={() => setPosunout(faktury.indexOf(p))}><CalendarPlus className="h-4 w-4" /> Posunout příští fakturu{faktury.length > 1 ? ` (${p.ucet})` : ""}</button>)}
          {faktury.map((p) => (p.status === "active"
            ? <button key={`${p.ucet}-${p.periodic_id}`} type="button" className="btn btn-secondary" disabled={busy} onClick={() => zmenit(p, "zastavit")}><Pause className="h-4 w-4" /> Pozastavit fakturaci{faktury.length > 1 ? ` (${p.ucet})` : ""}</button>
            : <button key={`${p.ucet}-${p.periodic_id}`} type="button" className="btn btn-primary" disabled={busy} onClick={() => zmenit(p, "spustit")}><Play className="h-4 w-4" /> Spustit fakturaci{faktury.length > 1 ? ` (${p.ucet})` : ""}</button>))}
        </div>
      )}
      {posunout !== null && faktury[posunout] && <PosunoutOkno p={faktury[posunout]} onClose={() => setPosunout(null)} onHotovo={() => setPosunout(null)} />}
      {jednorazova && <JednorazovaOkno klientId={klientId} klient={data.klient} maPravidelnou={faktury.some((p) => p.status === "active")} onClose={() => setJednorazova(false)} onHotovo={() => setJednorazova(false)} />}
      {zalozit && <ZalozitOkno klientId={klientId} klient={data.klient} vychozi={data.vychozi_zalozeni || null} onClose={() => setZalozit(false)} onHotovo={() => setZalozit(false)} />}
      {upravit !== null && faktury[upravit] && <UpravitFakturuOkno p={faktury[upravit]} busy={busy} onClose={() => setUpravit(null)} onUlozit={(v) => ulozitUpravu(faktury[upravit], v)} />}
    </div>
  );
}
